import axios from "axios";
import { PutAlertStatusProps, GetAlertProps } from "../types/alert";
import {
  SignInData,
  SignUpData,
  EllieCenterUserMayEdit,
  CreateUserData,
  RawHealthSystem,
  UserWithRole,
  User,
} from "../types/user";
import { Companion, Patient } from "../types/supportNetwork";
import { GetEventProps } from "../types/event";
import { UserInfo } from "../models";
import { UserWithRolePagination } from "../types/user";
import { updateUser } from "../redux/states/user";

axios.defaults.baseURL = process.env.REACT_APP_API_HOST;

export async function getAlerts({ params = {} }: GetAlertProps) {
  const resp = await axios.get(`/v3/bo/alerts/`, { params });
  return resp.data;
}

export async function getEvents({ params = {} }: GetEventProps) {
  const resp = await axios.get(`/v3/bo/events/`, { params });
  return resp.data;
}

export async function postLogin({ email, password }: SignInData) {
  const resp = await axios.post("/user/login", { email, password });
  return resp.data;
}

export async function putAlertStatus({
  id,
  status,
  body,
}: PutAlertStatusProps) {
  const resp = await axios.put(
    `/alert/edit-alert-ec?alertId=${id}&newStatus=${status}`,
    body
  );
  return resp;
}

export async function putUser(user: EllieCenterUserMayEdit) {
  const resp = await axios.put("/user", user);
  return resp.data;
}

export async function postSignUp({
  first_name,
  last_name,
  email,
  anura_email,
  shift,
  password,
  role,
}: SignUpData) {
  const resp = await axios.post("/user/create-user", {
    first_name,
    last_name,
    email,
    anura_email,
    shift,
    password,
    role,
  });
  return resp.data;
}

export async function createUser({
  plan_type,
  first_name,
  last_name,
  country_code,
  phone,
  email,
  support_network_role,
}: CreateUserData) {
  const resp = await axios.post(
    `/support-network/ellieCenter?plan_type=${plan_type}`,
    {
      first_name,
      last_name,
      country_code,
      phone,
      email,
      support_network_role,
    }
  );
  return resp.data;
}

export async function createHealthSystem({
  name,
  address,
  emergency_phone_number,
  contact_phone_number,
  email,
}: RawHealthSystem) {
  const resp = await axios.post(`/health-system`, {
    name,
    address,
    emergency_phone_number,
    contact_phone_number,
    email,
  });
  return resp.data;
}

export async function postAgentContact(patientId: string) {
  const resp = await axios.post(`/agent-contact?patientId=${patientId}`);
  return resp.data;
}

//////Nuevos endpoints BO v2/////////

export async function editAc(companion: Partial<Companion>) {
  const resp = await axios.patch(`/v3/bo/user/${companion.id}`, companion);
  return resp.data;
}

export async function editPatient(companion: Partial<Patient>) {
  const resp = await axios.patch(`/v3/bo/user/${companion.id}`, companion);
  return resp.data;
}

export async function editUser(user: Partial<UserInfo>) {
  if (!user?.id) {
    throw new Error("editUser requires user.id");
  }
  const resp = await axios.patch(`/v3/bo/user/${user.id}`, user);
  return resp.data;
}

export async function editPatientHealthSystem(
  user: Partial<Patient>,
  companion: Partial<Patient>
) {
  user = {
    ...user,
    health_system_affiliate_number: companion.health_system_affiliate_number,
    health_system_plan: companion.health_system_plan,
    health_system_data: companion.health_system_data,
  };
  const resp = await axios.patch(`/v3/bo/user/${companion.id}`, user);
  return resp.data;
}

export async function putEditPassword(body: {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}) {
  const resp = await axios.put(`/v3/bo/user/changer-password`, body);
  return resp.data;
}

export async function requestResetPassword(body: { email: string }) {
  const resp = await axios.post(`/v3/bo/user/request-reset-password`, body);
  return resp.data;
}

export async function sendResetPassword(body: {
  token: string;
  newPassword: string;
  confirmPassword: string;
}) {
  const resp = await axios.post(`/v3/bo/user/reset-password`, body);
  return resp.data;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function getUsersWithRole(queryParams: any) {
  const resp = await axios.get(
    `/v3/bo/users_roles?search=${queryParams.search}&role=${queryParams.role}&company_client=${queryParams.company_client}&page=${queryParams.page}&limit=${queryParams.limit}`
  );
  return resp.data;
}

// El JWT del BO no trae el company_client_id del operador de forma confiable.
// Para superadmins/admins lo resolvemos consultando la lista /users_roles filtrada por
// su email y lo guardamos en el user state, así useAuth puede gatear features de Ellie Care General.
export async function loadOperatorCompanyClient(
  user: { email?: string; role?: string } | undefined,
  dispatch: (action: ReturnType<typeof updateUser>) => void
) {
  if (!user?.email) return;
  if (user.role !== "superadmin" && user.role !== "admin") return;
  try {
    const res: UserWithRolePagination = await getUsersWithRole({
      search: user.email,
      role: "",
      company_client: "",
      page: 1,
      limit: 10,
    });
    const match = res?.users?.find(
      (u) => u.email?.toLowerCase() === user.email?.toLowerCase()
    );
    if (match?.company_client_id) {
      dispatch(updateUser({ company_client_id: match.company_client_id }));
    }
  } catch (error) {
    console.error("No se pudo cargar el company_client del operador", error);
  }
}

export async function editUserRole(user: Partial<UserWithRole>) {
  const resp = await axios.patch(`/v3/bo/users_roles/${user.id}`, user);
  return resp.data;
}

export async function getUserbyId(id: string) {
  const resp = await axios.get(`/v3/bo/user/${id}`);
  return resp.data;
}

export async function getAllRoles(params: { company_client_id: string }) {
  const resp = await axios.get(`/v3/bo/roles`, { params });
  return resp.data;
}

export async function createOperationalUser(user: Partial<User>) {
  const resp = await axios.post(`/v3/bo/users_roles`, user);
  return resp.data;
}
